import { useRef, useState, useLayoutEffect } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Check, X } from "lucide-react";
import "./multi-select-animations.css"; // <-- Add this import

export function MultiSelect({
  options,
  value = [],
  onChange,
  placeholder = "Select options",
}: {
  options: { label: string; value: string }[];
  value: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
}) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [popoverWidth, setPopoverWidth] = useState<number>(220);

  useLayoutEffect(() => {
    if (triggerRef.current) {
      setPopoverWidth(triggerRef.current.offsetWidth);
    }
  }, [triggerRef.current, value, options]);

  const toggleValue = (val: string) => {
    if (value.includes(val)) {
      onChange(value.filter((v) => v !== val));
    } else {
      onChange([...value, val]);
    }
  };

  const removeValue = (val: string) => {
    onChange(value.filter((v) => v !== val));
  };

  const selectedOptions = options.filter((opt) => value.includes(opt.value));

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          variant="outline"
          role="combobox"
          className="w-full flex flex-wrap gap-1 justify-between min-h-[2.5rem] items-center"
        >
          {selectedOptions.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {selectedOptions.map((opt) => (
                <span key={opt.value} className="flex items-center px-2 py-1 bg-muted rounded-full text-xs">
                  {opt.label}
                  <button
                    type="button"
                    className="ml-1 text-muted-foreground hover:text-destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeValue(opt.value);
                    }}
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        style={{ width: popoverWidth }}
        align="center"
        side="bottom"
        className="p-2 space-y-2 animate-slide-down"
      >
        {options.map((opt) => (
          <div
            key={opt.value}
            className="flex items-center justify-between space-x-2 cursor-pointer hover:bg-accent px-2 py-1 rounded"
            onClick={() => toggleValue(opt.value)}
          >
            <span className="text-sm">{opt.label}</span>
            <span className="w-5 h-5 flex items-center justify-center">
              {value.includes(opt.value) && <Check size={16} className="text-primary" />}
            </span>
          </div>
        ))}
      </PopoverContent>
    </Popover>
  );
}
